import { isStripeBillingStrategy } from '@ctct/domain-layer';
import { BREAKPOINT_MINS } from '@ctct/presentation-layer';
import {
  useIs3PLCustomer,
  useIsLegacyPlanGroup,
  useIsGuruLiteOfferPartner,
  useIsSocialStarter,
  useIsSuspendedFacade,
  useBillingStrategy,
} from '@ctct/state-server-layer';

import { useIsMobile } from './useIsMobile';
import { useIsSmsBundle } from './useIsSmsBundle';

export type UsePricingConfiguratorVisibilityOptions = {
  isCheckoutPage?: boolean;
};

/**
 * Hook to determine visibility of pricing configurator and its child widgets.
 * Fetches all required data internally, including SMS-bundle mode from the URL (`?aff-sms-bundle=true`).
 */
export const usePricingConfiguratorVisibility = ({
  isCheckoutPage = false,
}: UsePricingConfiguratorVisibilityOptions = {}) => {
  const isSmsBundle = useIsSmsBundle();
  const { isMobile } = useIsMobile(BREAKPOINT_MINS.mdWide);
  const isSmsBundleMWebCheckout = isCheckoutPage && isSmsBundle && isMobile;

  const { is3PLCustomer } = useIs3PLCustomer();
  // Legacy (non-3PL) groups are never a 3PL customer, so they need their own customer check;
  // otherwise legacy customers see the first-time-pay widgets (currency, contacts tier, non-profit).
  const { isLegacyCustomer } = useIsLegacyPlanGroup();
  const isCustomer = Boolean(is3PLCustomer || isLegacyCustomer);
  const { isSocialStarter, isCustomer: isSocialStarterCustomer } = useIsSocialStarter();
  const { isGuruLiteOfferPartner } = useIsGuruLiteOfferPartner();
  const { data: isSuspendedData, isLoading: isLoadingIsSuspended } = useIsSuspendedFacade();
  const isSuspended = !isLoadingIsSuspended && isSuspendedData;
  const billingStrategy = useBillingStrategy();
  const { phase2Enabled: isStripePhase2EnabledData, isStripeViaBillingAccount } = billingStrategy;

  const isStripeEnabled = isStripeBillingStrategy(billingStrategy);
  const isStripePhase2Enabled = Boolean(isStripePhase2EnabledData);
  const isStripeCustomer = Boolean(isCustomer && isStripeViaBillingAccount);
  const isStripeCustomerWithoutPhase2 = isStripeCustomer && !isStripePhase2Enabled;
  const isNonStripeCustomer = isCustomer && !isStripeViaBillingAccount;

  // Guru Lite offer: lock checkout to monthly billing and 0-500 contacts (hide tier/prepay selectors).
  if (isGuruLiteOfferPartner) {
    return {
      shouldHidePricingConfigurator: false,
      shouldHideCurrencyWidget: false,
      shouldHideContactsTierSelectWidget: true,
      shouldHideOrderSummaryContactsTierLabel: true,
      shouldHideNonProfitWidget: false,
      shouldHidePrepaySelectWidget: true,
      shouldHideSmsBundleTierSelectWidget: true,
    };
  }

  // Special handling for SocialStarter:
  // - for trailers show only currency selector.
  // - for customers hide everything
  if (isSocialStarter) {
    return {
      shouldHidePricingConfigurator: isSocialStarterCustomer || isSmsBundleMWebCheckout,
      shouldHideCurrencyWidget: isSocialStarterCustomer,
      shouldHideContactsTierSelectWidget: true,
      shouldHideOrderSummaryContactsTierLabel: true,
      shouldHideNonProfitWidget: true,
      shouldHidePrepaySelectWidget: true,
      shouldHideSmsBundleTierSelectWidget: true,
    };
  }

  // Hide pricing configurator when:
  // 1. Account is suspended, OR
  // 2. Customer, but not on stripe
  // 3. Customer is on stripe, but Phase 2 is not enabled
  // 4. SMS-bundle URL flow on mobile within the checkout page (uses dedicated mWeb layout)
  const shouldHidePricingConfigurator =
    isSuspended || isNonStripeCustomer || isStripeCustomerWithoutPhase2 || isSmsBundleMWebCheckout;

  const shouldHideCurrencyWidget = isStripeCustomer;
  const shouldHideContactsTierSelectWidget = isStripeCustomer;
  const shouldHideNonProfitWidget = isStripeEnabled && !isStripePhase2Enabled;
  const shouldHidePrepaySelectWidget = isStripeEnabled && !isStripePhase2Enabled;
  const shouldHideSmsBundleTierSelectWidget = !isSmsBundle || isCustomer;

  const shouldHideOrderSummaryContactsTierLabel = shouldHideContactsTierSelectWidget;

  return {
    shouldHidePricingConfigurator,
    shouldHideCurrencyWidget,
    shouldHideContactsTierSelectWidget,
    shouldHideOrderSummaryContactsTierLabel,
    shouldHideNonProfitWidget,
    shouldHidePrepaySelectWidget,
    shouldHideSmsBundleTierSelectWidget,
  };
};

export type PricingConfiguratorVisibility = ReturnType<typeof usePricingConfiguratorVisibility>;
